import React, { useEffect, useMemo, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Checkbox, FormControlLabel, Radio, RadioGroup, Stack, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { z } from 'zod';

import { DialogStyled, FormInputNumberController, FormLanguageInputController } from '@/components';
import {
  amountSchema,
  HandleQuizData,
  Language,
  languageSchema,
  onQueryError,
  onQuerySuccess,
  Quiz,
  useMenu,
} from '@/core';
import { LanguageTypes } from '@/features';
import palette from '@/theme/palette.ts';

type FormData = {
  name: Language;
  multipleChoice: boolean;
  required: boolean;
  minChoices: number;
  maxChoices?: number;
};

const schema = z
  .object({
    name: languageSchema,
    multipleChoice: z.boolean(),
    required: z.boolean(),
    minChoices: amountSchema,
    maxChoices: amountSchema.optional(),
  })
  .superRefine(({ minChoices, maxChoices }, ctx) => {
    if (minChoices !== undefined && maxChoices !== undefined && minChoices >= maxChoices) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['minChoices', 'maxChoices'],
      });
    }
  });

const RadioLabel = ({ label, infoLabel }: { label: string; infoLabel: string }) => {
  return (
    <Stack direction="row" alignItems="center" gap={1} sx={{ transition: 'all 0.3s ease-in-out' }}>
      <Typography variant="body1" fontWeight={700}>
        {label}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {infoLabel}
      </Typography>
    </Stack>
  );
};

const CheckboxLabel = ({ label, infoLabel }: { label: string; infoLabel: string }) => (
  <Typography variant="body2">
    {label}
    <span style={{ color: palette.text.secondary, marginLeft: '6px' }}>{infoLabel}</span>
  </Typography>
);

type DialogHandleVariantProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  defaultValues?: FormData;
  quiz?: Quiz;
};

export const DialogHandleVariant = ({
  open,
  setOpen,
  defaultValues,
  quiz,
}: DialogHandleVariantProps) => {
  const isEdit = useMemo(() => quiz !== undefined, [quiz]);

  const [selectedLanguage, setSelectedLanguage] = useState<LanguageTypes>('cs');
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const {
    mutations: { createMenuQuizMutation, updateMenuQuizMutation, deleteMenuQuizMutation },
  } = useMenu();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const methods = useForm<FormData>({
    defaultValues:
      isEdit && defaultValues
        ? defaultValues
        : {
            name: { en: '', cs: '', de: '' },
            multipleChoice: false,
            required: false,
            minChoices: 0,
            maxChoices: 1,
          },
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    if (open && !isEdit) {
      methods.reset({
        name: { en: '', cs: '', de: '' },
        multipleChoice: false,
        required: false,
        minChoices: 0,
        maxChoices: 1,
      });
    }
  }, [open, isEdit, methods]);

  const {
    watch,
    setValue,
    handleSubmit,
    formState: { errors },
  } = methods;

  const multipleChoice = watch('multipleChoice');
  const required = watch('required');

  const handleChoiceChange = (event: React.ChangeEvent<EventTarget>) => {
    const isMultipleChoice = (event.target as HTMLInputElement).value === 'multipleChoice';
    setValue('multipleChoice', isMultipleChoice);
    setValue('minChoices', 0);
    setValue('maxChoices', isMultipleChoice ? undefined : 1);
    setValue('required', false);
  };

  const handleSingleChoiceClicked = (event: React.ChangeEvent<EventTarget>) => {
    const isRequired = (event.target as HTMLInputElement).checked;
    setValue('minChoices', isRequired ? 1 : 0);
    setValue('maxChoices', 1);
    setValue('required', isRequired);
  };

  const handleMultiChoiceClicked = (event: React.ChangeEvent<EventTarget>) => {
    const isRequired = (event.target as HTMLInputElement).checked;
    setValue('minChoices', isRequired ? 1 : 0);
    if (isRequired) {
      const currentMaxChoices = watch('maxChoices');
      if (currentMaxChoices === undefined || currentMaxChoices === null) {
        setValue('maxChoices', 2);
      }
    } else {
      setValue('maxChoices', undefined);
    }
    setValue('required', isRequired);
  };

  const multipleChoiceErrorWhileSingleChoiceActive =
    (errors.minChoices?.message || errors.maxChoices?.message) && !multipleChoice;
  const noErrors = !Object.values(errors).some((error) => error);

  const onSuccess = () => {
    const successMessage = isEdit ? t('VARIANT.EDIT_SUCCESS') : t('VARIANT.CREATE_SUCCESS');
    onQuerySuccess({
      queryClient,
      queryKeysToInvalidate: [
        ['menu', Number(id)],
        ['menuQuizzes', Number(id)],
      ],
      successMessage,
      callback: () => setOpen(false),
    });
  };

  const onError = () => {
    const errorMessage = isEdit ? t('VARIANT.EDIT_FAILURE') : t('VARIANT.CREATE_FAILURE');
    onQueryError({ errorMessage, callback: () => setOpen(false) });
  };

  const onSubmit = async (data: FormData) => {
    if (noErrors || multipleChoiceErrorWhileSingleChoiceActive) {
      const handleQuizData: HandleQuizData = {
        name: data.name,
        min_choices: data.minChoices,
        max_choices: data.maxChoices || null,
        hidden: false,
      };

      if (isEdit) {
        const updateResponse = await updateMenuQuizMutation.mutateAsync({
          quizId: quiz!.id,
          data: handleQuizData,
        });
        if (updateResponse.status === 200 && updateResponse.data) {
          onSuccess();
        } else {
          onError();
        }
      } else {
        const createResponse = await createMenuQuizMutation.mutateAsync({
          menuId: Number(id),
          data: handleQuizData,
        });
        if (createResponse.status === 200 && createResponse.data) {
          onSuccess();
        } else {
          onError();
        }
      }
    }
  };

  const onDeleteSuccess = () => {
    onQuerySuccess({
      queryClient,
      queryKeysToInvalidate: [
        ['menu', Number(id)],
        ['menuQuizzes', Number(id)],
      ],
      successMessage: t('VARIANT.DELETE_SUCCESS'),
      callback: () => setOpen(false),
    });
  };

  const handleDeleteButtonClick = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDeleteClick = async () => {
    setIsDeleteDialogOpen(false);
    const response = await deleteMenuQuizMutation.mutateAsync(quiz!.id);
    if (response.status === 200 && response.data) {
      onDeleteSuccess();
    } else {
      onError();
    }
  };

  return (
    <>
      <DialogStyled
        open={isDeleteDialogOpen}
        setOpen={setIsDeleteDialogOpen}
        title={t('VARIANT.REALLY_WISH_TO_DELETE')}
        actionButtonLabel={t('GLOBAL.DELETE')}
        onActionButtonClick={handleConfirmDeleteClick}
      />
      <DialogStyled
        open={open}
        setOpen={setOpen}
        title={t('VARIANT.COMPOSITION_EDIT')}
        onActionButtonClick={handleSubmit(onSubmit)}
        actionButtonLabel={isEdit ? t('GLOBAL.EDIT') : t('GLOBAL.ADD')}
        onSecondaryActionButtonClick={isEdit ? handleDeleteButtonClick : undefined}
        secondaryActionButtonLabel={t('VARIANT.DELETE')}
        isCorrectlyFilledOut={noErrors}
      >
        <FormProvider {...methods}>
          <FormLanguageInputController
            label={t('VARIANT.NAME')}
            controllerName={'name'}
            error={!!errors.name}
            errorText={t('GLOBAL.MIN_ONE_LANG')}
            helperText={t('VARIANT.SCREEN_MAIN_TEXT')}
            selectedLanguage={selectedLanguage}
            setSelectedLanguage={setSelectedLanguage}
          />
          <Typography variant="body1" sx={{ my: 2 }}>
            {t('VARIANT.DETERMINE_ANSWER_SELECT_COUNT')}
          </Typography>
          <RadioGroup
            name="radio-buttons-group"
            sx={{ transition: 'all 0.3s ease-in-out' }}
            value={multipleChoice ? 'multipleChoice' : 'singleChoice'}
          >
            <FormControlLabel
              control={<Radio checked={!multipleChoice} />}
              label={
                <RadioLabel
                  label={t('VARIANT.SINGLE_CHOICE')}
                  infoLabel={t('VARIANT.SINGLE_CHOICE_DESCRIPTION')}
                />
              }
              value="singleChoice"
              onChange={handleChoiceChange}
            />
            {!multipleChoice && (
              <FormControlLabel
                control={<Checkbox checked={required} sx={{ minWidth: '42px' }} />}
                label={
                  <CheckboxLabel
                    label={t('VARIANT.MANDATORY')}
                    infoLabel={t('VARIANT.MANDATORY_DESCRIPTION')}
                  />
                }
                onChange={handleSingleChoiceClicked}
                sx={{ ml: 3 }}
              />
            )}
            <FormControlLabel
              value="multipleChoice"
              control={<Radio checked={multipleChoice} />}
              label={
                <RadioLabel
                  label={t('VARIANT.MULTICHOICE')}
                  infoLabel={t('VARIANT.MULTICHOICE_DESCRIPTION')}
                />
              }
              onChange={handleChoiceChange}
            />
            {multipleChoice && (
              <FormControlLabel
                value="required"
                control={<Checkbox checked={required} sx={{ minWidth: '42px' }} />}
                label={
                  <CheckboxLabel
                    label={t('VARIANT.MANDATORY_MULTICHOICE')}
                    infoLabel={t('VARIANT.MANDATORY_MULTICHOICE_DESCRIPTION')}
                  />
                }
                onChange={handleMultiChoiceClicked}
                sx={{ ml: 3 }}
              />
            )}
            {multipleChoice && required && (
              <Stack direction="row" alignItems="center" gap={1} sx={{ mt: 3, ml: 5 }}>
                <FormInputNumberController
                  label={t('VARIANT.MIN_COUNT')}
                  controllerName={'minChoices'}
                  error={!!errors.minChoices}
                  errorText={t('VARIANT.MIN_MAX_CHOICE_SHORT_WARNING')}
                  isWholeNumber
                  sx={{ maxWidth: '170px' }}
                />
                <span className="material-symbols-rounded" style={{ color: palette.primary.main }}>
                  remove
                </span>
                <FormInputNumberController
                  label={t('VARIANT.MAX_COUNT')}
                  controllerName={'maxChoices'}
                  error={!!errors.minChoices}
                  errorText={' '}
                  isWholeNumber
                  sx={{ maxWidth: '170px' }}
                />
              </Stack>
            )}
            {errors.minChoices?.message && errors.maxChoices?.message && (
              <Typography variant="body2" color="error">
                {errors.minChoices?.message}
              </Typography>
            )}
          </RadioGroup>
        </FormProvider>
      </DialogStyled>
    </>
  );
};
